import React, { useEffect, useState } from "react";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { useParams } from "react-router-dom";
import { getRateByResortId } from "../../../../../../../apis/property";
import {
  XIcon,
  CashIcon,
  UserGroupIcon,
  CalendarIcon,
  CollectionIcon,
  CurrencyDollarIcon,
} from "@heroicons/react/outline";

const CreateRatePrice = ({ isOpen, onClose, fetchResorts }) => {
  const [rate, setRate] = useState(null);
  const { id } = useParams();
  const baseUrl = process.env.REACT_APP_BASE_URL;
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    base_price: "",
    single_occupancy: "",
    double_occupancy: "",
    extra_adult: "",
    currency_code: "NGN",
    start_date: "",
    end_date: "",
    room_rate_id: "",
    resort_id: id,
  });

  const fetchRate = async () => {
    try {
      const response = await getRateByResortId(id);
      const rates = response.data.rates || [];
      setRate(rates.flatMap((item) => {
        const mappings = item.room_rates || (item.room_types || []).map((roomType) => ({
          id: roomType.room_rate_id,
          room_type: roomType,
        }));
        return mappings.filter((mapping) => mapping?.id).map((mapping) => ({
          id: mapping.id,
          name: `${item.name || item.code} · ${mapping.room_type?.name || mapping.room_type?.code || "Room type"}`,
        }));
      }));
    } catch (error) {
      console.log("Error fetching rate");
    }
  };

  // The modal loads its option list once when it mounts.
  useEffect(() => {
    fetchRate();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prevData) => ({
      ...prevData,
      [name]: value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const accessToken = localStorage.getItem("accessToken");

      const response = await fetch(`${baseUrl}/rate/price`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${accessToken}`,
        },
        body: JSON.stringify(formData),
      });

      if (response.ok) {
        toast.success("Rate price created successfully");
        setFormData({
          base_price: "",
          single_occupancy: "",
          double_occupancy: "",
          extra_adult: "",
          currency_code: "NGN",
          start_date: "",
          end_date: "",
          room_rate_id: "",
          resort_id: id,
        });
        onClose();
      } else {
        toast.error("Failed to create rate price");
        console.error("Failed to create rate price");
      }
    } catch (error) {
      toast.error("Error occurred while creating rate price");
      console.error("Error occurred while creating rate price:", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <>
      <div className="fixed inset-0 z-50 overflow-y-auto">
        {/* Backdrop */}
        <div
          className="fixed inset-0 bg-black bg-opacity-50 backdrop-blur-sm transition-opacity"
          onClick={onClose}
        ></div>

        {/* Modal */}
        <div className="flex min-h-screen items-center justify-center p-4">
          <div className="relative w-full max-w-2xl transform transition-all">
            <div className="modal-container">
              {/* Header */}
              <div className="modal-header">
                <div className="flex items-center space-x-3">
                  <div className="modal-icon">
                    <CashIcon className="h-6 w-6" />
                  </div>
                  <div>
                    <h3 className="modal-title">Add Rate Price</h3>
                    <p className="modal-subtitle">Configure pricing for different occupancies</p>
                  </div>
                </div>
                <button onClick={onClose} className="modal-close-btn" type="button">
                  <XIcon className="h-5 w-5" />
                </button>
              </div>

              {/* Form */}
              <form onSubmit={handleSubmit}>
                <div className="modal-body">
                  <div className="form-grid">
                    {/* Base Price */}
                    <div className="form-group">
                      <label htmlFor="base_price" className="form-label">
                        <CurrencyDollarIcon className="form-label-icon" />
                        Base Price *
                      </label>
                      <input
                        type="number"
                        id="base_price"
                        name="base_price"
                        value={formData.base_price}
                        onChange={handleChange}
                        className="form-input"
                        placeholder="e.g., 100"
                        required
                      />
                    </div>

                    {/* Currency */}
                    <div className="form-group">
                      <label htmlFor="currency_code" className="form-label"><CurrencyDollarIcon className="form-label-icon" />Currency *</label>
                      <input type="text" id="currency_code" name="currency_code" value={formData.currency_code} onChange={handleChange} className="form-input" maxLength="3" required placeholder="NGN" />
                    </div>

                    {/* Single Occupancy */}
                    <div className="form-group">
                      <label htmlFor="single_occupancy" className="form-label">
                        <UserGroupIcon className="form-label-icon" />
                        Single Occupancy *
                      </label>
                      <input
                        type="number"
                        id="single_occupancy"
                        name="single_occupancy"
                        value={formData.single_occupancy}
                        onChange={handleChange}
                        className="form-input"
                        placeholder="e.g., 80"
                        required
                      />
                    </div>

                    {/* Double Occupancy */}
                    <div className="form-group">
                      <label htmlFor="double_occupancy" className="form-label">
                        <UserGroupIcon className="form-label-icon" />
                        Double Occupancy *
                      </label>
                      <input
                        type="number"
                        id="double_occupancy"
                        name="double_occupancy"
                        value={formData.double_occupancy}
                        onChange={handleChange}
                        className="form-input"
                        placeholder="e.g., 120"
                        required
                      />
                    </div>

                    {/* Extra Adult */}
                    <div className="form-group">
                      <label htmlFor="extra_adult" className="form-label">
                        <UserGroupIcon className="form-label-icon" />
                        Extra Adult *
                      </label>
                      <input
                        type="number"
                        id="extra_adult"
                        name="extra_adult"
                        value={formData.extra_adult}
                        onChange={handleChange}
                        className="form-input"
                        placeholder="e.g., 30"
                        required
                      />
                    </div>

                    {/* Start Date */}
                    <div className="form-group">
                      <label htmlFor="start_date" className="form-label">
                        <CalendarIcon className="form-label-icon" />
                        Start Date *
                      </label>
                      <input
                        type="date"
                        id="start_date"
                        name="start_date"
                        value={formData.start_date}
                        onChange={handleChange}
                        className="form-input"
                        required
                      />
                    </div>

                    {/* End Date */}
                    <div className="form-group">
                      <label htmlFor="end_date" className="form-label">
                        <CalendarIcon className="form-label-icon" />
                        End Date *
                      </label>
                      <input
                        type="date"
                        id="end_date"
                        name="end_date"
                        value={formData.end_date}
                        onChange={handleChange}
                        className="form-input"
                        required
                      />
                    </div>

                    {/* Room Rate */}
                    <div className="form-group md:col-span-2">
                      <label htmlFor="room_rate_id" className="form-label">
                        <CollectionIcon className="form-label-icon" />
                            Room + rate mapping *
                      </label>
                      <select
                        id="room_rate_id"
                        name="room_rate_id"
                        value={formData.room_rate_id}
                        onChange={handleChange}
                        className="form-select"
                        required
                      >
                        <option value="">Select a room type and rate</option>
                        {rate?.map((item) => (
                          <option key={item.id} value={item.id}>
                            {item.name}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                </div>

                {/* Footer */}
                <div className="modal-footer">
                  <button
                    type="button"
                    onClick={onClose}
                    className="btn-secondary"
                    disabled={isSubmitting}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="btn-primary"
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? (
                      <>
                        <svg className="animate-spin h-5 w-5 mr-2" viewBox="0 0 24 24">
                          <circle
                            className="opacity-25"
                            cx="12"
                            cy="12"
                            r="10"
                            stroke="currentColor"
                            strokeWidth="4"
                            fill="none"
                          />
                          <path
                            className="opacity-75"
                            fill="currentColor"
                            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                          />
                        </svg>
                        Creating...
                      </>
                    ) : (
                      "Add Rate Price"
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>

      {/* Styles */}
      <style jsx>{`
        .modal-container {
          background: white;
          border-radius: 1rem;
          box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.25);
          overflow: hidden;
        }

        .modal-header {
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding: 1.5rem;
          background: linear-gradient(135deg, #14b8a6, #0d9488);
          border-bottom: 1px solid rgba(255, 255, 255, 0.1);
        }

        .modal-icon {
          width: 3rem;
          height: 3rem;
          background: rgba(255, 255, 255, 0.2);
          backdrop-filter: blur(10px);
          border-radius: 0.75rem;
          display: flex;
          align-items: center;
          justify-content: center;
          color: white;
        }

        .modal-title {
          font-size: 1.25rem;
          font-weight: 700;
          color: white;
          margin: 0;
        }

        .modal-subtitle {
          font-size: 0.875rem;
          color: rgba(255, 255, 255, 0.9);
          margin: 0.25rem 0 0 0;
        }

        .modal-close-btn {
          padding: 0.5rem;
          background: rgba(255, 255, 255, 0.2);
          border: none;
          border-radius: 0.5rem;
          color: white;
          cursor: pointer;
          transition: all 0.2s ease;
        }

        .modal-close-btn:hover {
          background: rgba(255, 255, 255, 0.3);
          transform: scale(1.1);
        }

        .modal-body {
          padding: 2rem;
          max-height: 60vh;
          overflow-y: auto;
        }

        .modal-body::-webkit-scrollbar {
          width: 8px;
        }

        .modal-body::-webkit-scrollbar-track {
          background: #f1f1f1;
          border-radius: 4px;
        }

        .modal-body::-webkit-scrollbar-thumb {
          background: #14b8a6;
          border-radius: 4px;
        }

        .modal-body::-webkit-scrollbar-thumb:hover {
          background: #0d9488;
        }

        .form-grid {
          display: grid;
          grid-template-columns: repeat(2, 1fr);
          gap: 1.5rem;
        }

        .form-group {
          display: flex;
          flex-direction: column;
          gap: 0.5rem;
        }

        .form-label {
          display: flex;
          align-items: center;
          font-size: 0.875rem;
          font-weight: 600;
          color: #374151;
        }

        .form-label-icon {
          width: 1rem;
          height: 1rem;
          margin-right: 0.5rem;
          color: #14b8a6;
        }

        .form-input,
        .form-select {
          width: 100%;
          padding: 0.75rem 1rem;
          border: 2px solid #e5e7eb;
          border-radius: 0.75rem;
          font-size: 0.875rem;
          transition: all 0.2s ease;
          background: #f9fafb;
        }

        .form-input:focus,
        .form-select:focus {
          outline: none;
          border-color: #14b8a6;
          background: white;
          box-shadow: 0 0 0 3px rgba(20, 184, 166, 0.1);
        }

        .form-select {
          cursor: pointer;
        }

        .modal-footer {
          display: flex;
          justify-content: flex-end;
          gap: 0.75rem;
          padding: 1.5rem;
          background: #f9fafb;
          border-top: 1px solid #e5e7eb;
        }

        .btn-secondary {
          padding: 0.75rem 1.5rem;
          background: white;
          border: 2px solid #e5e7eb;
          border-radius: 0.75rem;
          font-weight: 600;
          color: #374151;
          cursor: pointer;
          transition: all 0.2s ease;
        }

        .btn-secondary:hover:not(:disabled) {
          background: #f3f4f6;
          border-color: #d1d5db;
        }

        .btn-secondary:disabled {
          opacity: 0.5;
          cursor: not-allowed;
        }

        .btn-primary {
          padding: 0.75rem 1.5rem;
          background: linear-gradient(135deg, #14b8a6, #0d9488);
          border: none;
          border-radius: 0.75rem;
          font-weight: 600;
          color: white;
          cursor: pointer;
          transition: all 0.2s ease;
          display: inline-flex;
          align-items: center;
          justify-content: center;
        }

        .btn-primary:hover:not(:disabled) {
          background: linear-gradient(135deg, #0d9488, #0f766e);
          transform: translateY(-2px);
          box-shadow: 0 10px 15px -3px rgba(20, 184, 166, 0.4);
        }

        .btn-primary:disabled {
          opacity: 0.7;
          cursor: not-allowed;
        }

        @media (max-width: 768px) {
          .form-grid {
            grid-template-columns: 1fr;
          }
        }
      `}</style>
    </>
  );
};

export default CreateRatePrice;
